/**
 * `eval`-free formula evaluator for custom tables (02-constructor §7.2; legacy grammar chotqol-legacy §6):
 * numbers ("0.45"; "," separates function arguments), `+ - * / % ^`, parentheses, unary minus, functions `abs sqrt pow log ln exp sin cos
 * tan min max round`, constants `pi e`, row references `R1…Rn` (value of row n in the same column), `C` (column
 * number, 1-based) and `X` (the column's device value — the nearest device row above the formula row).
 * Errors throw `FormulaError`; the table shows "Xato" (no silent fallback to raw values).
 */

export class FormulaError extends Error {}

export interface FormulaScope {
  /** value of row `n` (1-based, as displayed) in the current column, or null */
  row: (n: number) => number | null;
  column: number;
  x: number | null;
}

type Token = { t: 'num'; v: number } | { t: 'id'; v: string } | { t: 'op'; v: string };

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j++;
      const raw = src.slice(i, j);
      const v = Number(raw);
      if (!Number.isFinite(v)) throw new FormulaError(`bad number ${raw}`);
      out.push({ t: 'num', v });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j]!)) j++;
      out.push({ t: 'id', v: src.slice(i, j) });
      i = j;
      continue;
    }
    if ('+-*/%^(),'.includes(ch) || ch === '−' || ch === '×' || ch === '÷') {
      out.push({ t: 'op', v: ch === '−' ? '-' : ch === '×' ? '*' : ch === '÷' ? '/' : ch });
      i++;
      continue;
    }
    throw new FormulaError(`unexpected "${ch}"`);
  }
  return out;
}

const FUNCS: Record<string, (...a: number[]) => number> = {
  abs: Math.abs,
  sqrt: Math.sqrt,
  pow: Math.pow,
  log: Math.log10,
  ln: Math.log,
  exp: Math.exp,
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  min: Math.min,
  max: Math.max,
  round: (v: number, d = 0) => Math.round(v * 10 ** d) / 10 ** d,
};

/** Evaluate a formula. A leading "=" is allowed. */
export function evaluateFormula(src: string, scope: FormulaScope): number {
  const tokens = tokenize(src.trim().replace(/^=/, ''));
  let pos = 0;
  const peek = () => tokens[pos];
  const take = () => tokens[pos++];
  const expectOp = (v: string) => {
    const tk = take();
    if (!tk || tk.t !== 'op' || tk.v !== v) throw new FormulaError(`expected "${v}"`);
  };
  const need = (v: number | null, what: string) => {
    if (v == null || !Number.isFinite(v)) throw new FormulaError(`${what} has no value`);
    return v;
  };

  // expr := term (('+'|'-') term)*
  const expr = (): number => {
    let v = term();
    for (let tk = peek(); tk?.t === 'op' && (tk.v === '+' || tk.v === '-'); tk = peek()) {
      take();
      v = tk.v === '+' ? v + term() : v - term();
    }
    return v;
  };
  // term := power (('*'|'/'|'%') power)*
  const term = (): number => {
    let v = power();
    for (let tk = peek(); tk?.t === 'op' && (tk.v === '*' || tk.v === '/' || tk.v === '%'); tk = peek()) {
      take();
      const r = power();
      v = tk.v === '*' ? v * r : tk.v === '/' ? v / r : v % r;
    }
    return v;
  };
  // power := unary ('^' power)?   (right-assoc)
  const power = (): number => {
    const base = unary();
    const tk = peek();
    if (tk?.t === 'op' && tk.v === '^') {
      take();
      return base ** power();
    }
    return base;
  };
  const unary = (): number => {
    const tk = peek();
    if (tk?.t === 'op' && (tk.v === '-' || tk.v === '+')) {
      take();
      return tk.v === '-' ? -unary() : unary();
    }
    return primary();
  };
  const primary = (): number => {
    const tk = take();
    if (!tk) throw new FormulaError('unexpected end');
    if (tk.t === 'num') return tk.v;
    if (tk.t === 'op' && tk.v === '(') {
      const v = expr();
      expectOp(')');
      return v;
    }
    if (tk.t === 'id') {
      const id = tk.v;
      const lower = id.toLowerCase();
      const next = peek();
      if (next?.t === 'op' && next.v === '(') {
        const fn = FUNCS[lower];
        if (!fn) throw new FormulaError(`unknown function ${id}`);
        take();
        const args: number[] = [];
        if (!(peek()?.t === 'op' && (peek() as { v: string }).v === ')')) {
          args.push(expr());
          while (peek()?.t === 'op' && (peek() as { v: string }).v === ',') {
            take();
            args.push(expr());
          }
        }
        expectOp(')');
        return fn(...args);
      }
      if (lower === 'pi') return Math.PI;
      if (lower === 'e') return Math.E;
      if (id === 'C' || id === 'c') return scope.column;
      if (id === 'X' || id === 'x') return need(scope.x, 'X');
      const ref = /^[Rr](\d+)$/.exec(id);
      if (ref) return need(scope.row(Number(ref[1])), id);
      throw new FormulaError(`unknown name ${id}`);
    }
    throw new FormulaError(`unexpected "${tk.v}"`);
  };

  const v = expr();
  if (pos < tokens.length) throw new FormulaError('unexpected input');
  if (!Number.isFinite(v)) throw new FormulaError('not a number');
  return v;
}

/** Parse a manual cell ("0,45", "12", "1 234.5") to a number, or null. */
export function parseNumber(text: string | null | undefined): number | null {
  if (text == null) return null;
  const cleaned = text.replace(/[\s  ]/g, '').replace(',', '.').replace('−', '-');
  if (!cleaned || !/^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(cleaned)) return null;
  const v = Number(cleaned);
  return Number.isFinite(v) ? v : null;
}
